// Lone Rider — Moto Lights comparison table
//
// Fills in the two things on that table which must not be typed by hand:
//
//   [data-ml-from-price="mtl4|mtl9|bundle"]  the lowest PURCHASABLE price for that
//                                            light set, in the shop's money format
//   [data-ml-compare-image="..."]            the light set's product photo, when the
//                                            section has not been given one
//
// Everything else on the table is copy, and lives in the section settings.
//
// The price comes from MotoLightsConfigurator.fromPrice(), which walks the merged
// catalogue and skips anything coming-soon or out of stock — the same rule the
// buybox uses to decide whether a kit can be bought. So the "from" price is always a
// price a rider could actually pay today, and it can never disagree with the buybox.
//
// If the catalogue cannot load, the price cells keep whatever the Liquid rendered
// (a dash) rather than showing a wrong number.
(() => {
  'use strict';

  function fill(root, config) {
    root.querySelectorAll('[data-ml-from-price]').forEach((el) => {
      const set = el.getAttribute('data-ml-from-price');
      const cents = config.fromPrice(set);

      // Null means nothing in that set is buyable right now. Hide the whole "from"
      // line rather than print a zero or an empty currency symbol.
      const row = el.closest('[data-ml-price-row]') || el;
      if (cents === null || cents === undefined) {
        row.hidden = true;
        return;
      }
      row.hidden = false;
      el.textContent = config.formatMoney(cents);
    });

    root.querySelectorAll('[data-ml-compare-image]').forEach((img) => {
      // Only when the merchant has not chosen one in the customizer.
      if (img.getAttribute('src')) return;
      const src = config.setImage(img.getAttribute('data-ml-compare-image'));
      if (!src) return;
      const sized = (src.indexOf('cdn.shopify.com') > -1 || src.indexOf('/cdn/') > -1)
        ? src.replace(/(\.(?:jpe?g|png|webp|gif))(\?|$)/i, '_600x$1$2')
        : src;
      img.setAttribute('src', sized);
      img.hidden = false;
    });
  }

  // ---------- mobile carousel dots ----------
  //
  // The cards scroll sideways on a phone, one at a time. Without these there is
  // nothing to say the bundle is one of three choices.
  //
  // Positions are read from offsetLeft rather than from DOM order, because the bundle
  // is moved to the front on mobile with `order: -1` — CSS order changes what the
  // rider sees but not the order of the elements, so anything keyed off DOM order
  // would highlight the wrong dot.
  function bindDots(root) {
    if (root.__mlDotsBound) return;
    root.__mlDotsBound = true;

    const grid = root.querySelector('[data-ml-compare-grid]');
    const dotsWrap = root.querySelector('[data-ml-compare-dots]');
    if (!grid || !dotsWrap) return;

    const dots = Array.prototype.slice.call(dotsWrap.querySelectorAll('[data-ml-dot]'));
    if (!dots.length) return;

    const visualCards = () =>
      Array.prototype.slice.call(grid.querySelectorAll('.mlc__card'))
        .sort((a, b) => a.offsetLeft - b.offsetLeft);

    function setActive(i) {
      dots.forEach((d, n) => {
        const on = n === i;
        d.classList.toggle('is-active', on);
        d.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    // Which card is nearest the middle of the viewport right now.
    function currentIndex() {
      const cards = visualCards();
      const mid = grid.scrollLeft + grid.clientWidth / 2;
      let best = 0;
      let bestGap = Infinity;
      cards.forEach((card, i) => {
        const centre = card.offsetLeft + card.clientWidth / 2;
        const gap = Math.abs(centre - mid);
        if (gap < bestGap) { bestGap = gap; best = i; }
      });
      return best;
    }

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        const card = visualCards()[i];
        if (!card) return;
        const left = card.offsetLeft - (grid.clientWidth - card.clientWidth) / 2;
        if (typeof grid.scrollTo === 'function') {
          grid.scrollTo({ left: left, behavior: 'smooth' });
        } else {
          grid.scrollLeft = left;
        }
        setActive(i);
      });
    });

    // Throttled to animation frames: scroll fires far more often than this needs to run.
    let ticking = false;
    grid.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setActive(currentIndex());
        ticking = false;
      });
    }, { passive: true });

    setActive(currentIndex());
  }

  function init(scope) {
    const roots = (scope || document).querySelectorAll('[data-ml-compare]');
    if (!roots.length) return;

    // The dots are independent of the catalogue, so they work even if it fails to load.
    roots.forEach(bindDots);

    if (!window.MotoLightsConfigurator) return;

    window.MotoLightsConfigurator.ready()
      .then((config) => roots.forEach((root) => fill(root, config)))
      .catch((e) => console.error('[LR moto lights] comparison prices unavailable', e));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
